const BASE = import.meta.env.DEV ? "/api" : (import.meta.env.VITE_API_BASE || "/api");

export interface Lead {
  lead_id: number;
  name: string | null;
  phone: string | null;
  email: string | null;
  stage: string;
  product: string;
  source: string;
  deal_amount: number | null;
  payment_status: string;
  assignee_user_id: number | null;
  assignee_username: string | null;
  last_activity_at: string | null;
  updated_at: string;
  created_at?: string;
}

export interface Message { id: number; direction: string; text: string | null; created_at: string; }
export interface TaskShort { id: number; title: string; due_at: string | null; done_at: string | null; }
export interface NoteShort { id: number; text: string; user_username: string | null; created_at: string; }

export interface LeadDetail extends Lead {
  messages: Message[];
  tasks: TaskShort[];
  notes: NoteShort[];
}

export interface Me {
  user_id: number;
  tg_id: number;
  tg_username: string | null;
  full_name: string | null;
  role: string;
}

function token(): string | null { return localStorage.getItem("crm_token"); }
export function setToken(t: string) { localStorage.setItem("crm_token", t); }
export function clearToken() { localStorage.removeItem("crm_token"); }
export function hasToken() { return Boolean(token()); }

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  const t = token();
  if (t) headers.set("Authorization", `Bearer ${t}`);
  const r = await fetch(`${BASE}${path}`, { ...init, headers });
  if (r.status === 401) { clearToken(); window.location.href = "/login"; throw new Error("unauthorized"); }
  if (!r.ok) {
    const text = await r.text().catch(() => "");
    throw new Error(text || `${r.status} ${r.statusText}`);
  }
  return r.json();
}

export const api = {
  me: () => call<Me>("/auth/me"),
  loginTelegram: (payload: Record<string, unknown>) =>
    call<{ token: string; role: string; user_id: number; tg_username: string | null; full_name: string | null }>(
      "/auth/telegram",
      { method: "POST", body: JSON.stringify(payload) },
    ),

  listLeads: (stage?: string) =>
    call<Lead[]>(`/leads${stage ? `?stage=${stage}` : ""}`),
  getLead: (id: number) => call<LeadDetail>(`/leads/${id}`),
  setStage: (id: number, stage: string) =>
    call<Lead>(`/leads/${id}/stage`, { method: "PATCH", body: JSON.stringify({ stage }) }),
  updateFinance: (id: number, patch: Record<string, unknown>) =>
    call<Lead>(`/leads/${id}/finance`, { method: "PATCH", body: JSON.stringify(patch) }),

  listTasks: (open_only = true) =>
    call<TaskShort[]>(`/tasks?open_only=${open_only}`),
  createTask: (lead_id: number, title: string, due_at: string | null) =>
    call<TaskShort>("/tasks", { method: "POST", body: JSON.stringify({ lead_id, title, due_at }) }),
  toggleTask: (id: number, done: boolean) =>
    call<TaskShort>(`/tasks/${id}`, { method: "PATCH", body: JSON.stringify({ done }) }),

  listSiteLeads: () =>
    call<Array<{
      id: number; external_id: string; at: string | null; name: string; contact: string;
      company: string | null; about: string | null; page: string | null; source: string | null; lang: string | null;
    }>>("/site-leads"),

  listVoiceCards: () =>
    call<Array<{
      id: number; session_id: string; at: string | null; name: string | null;
      contact: string | null; company: string | null; lang: string | null;
      duration: number; score: number | null; summary: string | null; booking_missed: boolean;
    }>>("/voice-cards"),

  addNote: (lead_id: number, text: string) =>
    call<NoteShort>("/notes", { method: "POST", body: JSON.stringify({ lead_id, text }) }),

  funnel: () => call<Array<{ stage: string; count: number; conversion_from_prev: number | null }>>("/analytics/funnel"),
  pipeline: () => call<Array<{ stage: string; count: number; amount: number }>>("/analytics/pipeline"),
  managers: () => call<Array<{
    user_id: number; tg_username: string | null; full_name: string | null;
    leads_active: number; notes_count: number; tasks_open: number; tasks_done: number;
    stage_changes: number; deals_won: number; revenue_won: number;
  }>>("/analytics/managers"),
  sources: () => call<Array<{ source: string; leads_total: number; leads_won: number; revenue_won: number; win_rate: number }>>("/analytics/sources"),
  trend: (days = 7) => call<{ days: Array<{ day: string; new_leads: number; paid: number; revenue: number }> }>(`/analytics/trend?days=${days}`),
};

export const STAGES: { key: string; title: string }[] = [
  { key: "new", title: "Новый лид" },
  { key: "in_work", title: "Взят в работу" },
  { key: "negotiation", title: "Переговоры" },
  { key: "invoice_sent", title: "Счёт выставлен" },
  { key: "paid", title: "Оплата получена" },
  { key: "closed_won", title: "Закрыт (win)" },
  { key: "closed_lost", title: "Отказ" },
];

export const PRODUCT_LABEL: Record<string, string> = {
  club: "Клуб 1500₽",
  hypnotherapy: "Гипнотерапевт $1700",
  retreat: "Ретрит $3600",
  practitioner: "Практик $7500",
  other: "Другое",
};

export const SOURCE_LABEL: Record<string, string> = {
  channel: "Канал",
  bot: "Бот",
  landing: "Лендинг",
  referral: "Реферал",
  unknown: "—",
};
